/**
 * `Stepper` — design §4's "a stepper in the dock; only the active step is expanded, earlier
 * steps show their choice and can be reopened." Generic over what a step's body is, so
 * `Uc3.tsx` owns all nine steps' own content and this owns only the accordion mechanics.
 *
 * The active step's id is `intel.step` (Task 6's store slice) rather than local state — a
 * use-case switch already resets it to 0 (`resetIntel`/`setIntelUseCase`), so a reader who
 * leaves UC3 mid-stepper and comes back starts over rather than reopening on a step whose
 * earlier choices `Uc3.tsx`'s own local state has also since reset.
 */
import type { ReactNode } from 'react';

export interface StepperStep {
  /** 1-based — `#uc3Step-<n>` reads it directly. */
  id: number;
  title: string;
  /** Printed on a collapsed step that has already been visited (`id < active`). */
  summary?: ReactNode;
  body: ReactNode;
}

export interface StepperProps {
  active: number;
  onSelect: (step: number) => void;
  steps: StepperStep[];
}

export function Stepper({ active, onSelect, steps }: StepperProps) {
  return (
    <div id="uc3Stepper" className="uc3-stepper">
      {steps.map((step) => {
        const isActive = step.id === active;
        const isDone = step.id < active;
        return (
          <div
            key={step.id}
            id={`uc3Step-${step.id}`}
            className={`uc3-step${isActive ? ' active' : ''}${isDone ? ' done' : ''}`}
          >
            <button
              type="button"
              className="uc3-step-head"
              aria-expanded={isActive}
              onClick={() => onSelect(step.id)}
            >
              <span className="uc3-step-num">{step.id}</span>
              <span className="uc3-step-title">{step.title}</span>
              {!isActive && step.summary ? (
                <span className="uc3-step-summary">{step.summary}</span>
              ) : null}
            </button>
            {isActive ? <div className="uc3-step-body">{step.body}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

export default Stepper;
